// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.
import { __awaiter, __extends, __generator } from "tslib";
import { BaseRequestPolicy } from "../policies/requestPolicy";
import { Constants } from "../util/constants";
import { HttpHeaders } from "../httpHeaders";
import { ExpiringAccessTokenCache } from "../credentials/accessTokenCache";
/**
 * Creates a new BearerTokenAuthenticationPolicy factory.
 *
 * @param credential The TokenCredential implementation that can supply the bearer token.
 * @param scopes The scopes for which the bearer token applies.
 */
export function bearerTokenAuthenticationPolicy(credential, scopes) {
    var tokenCache = new ExpiringAccessTokenCache();
    return {
        create: function (nextPolicy, options) {
            return new BearerTokenAuthenticationPolicy(nextPolicy, options, credential, scopes, tokenCache);
        }
    };
}
/**
 *
 * Provides a RequestPolicy that can request a token from a TokenCredential
 * implementation and then apply it to the Authorization header of a request
 * as a Bearer token.
 *
 */
var BearerTokenAuthenticationPolicy = /** @class */ (function (_super) {
    __extends(BearerTokenAuthenticationPolicy, _super);
    /**
     * Creates a new BearerTokenAuthenticationPolicy object.
     *
     * @param nextPolicy The next RequestPolicy in the request pipeline.
     * @param options Options for this RequestPolicy.
     * @param credential The TokenCredential implementation that can supply the bearer token.
     * @param scopes The scopes for which the bearer token applies.
     * @param tokenCache The cache for the most recent AccessToken returned from the TokenCredential.
     */
    function BearerTokenAuthenticationPolicy(nextPolicy, options, credential, scopes, tokenCache) {
        var _this = _super.call(this, nextPolicy, options) || this;
        _this.credential = credential;
        _this.scopes = scopes;
        _this.tokenCache = tokenCache;
        return _this;
    }
    /**
     * Applies the Bearer token to the request through the Authorization header.
     * @param webResource
     */
    BearerTokenAuthenticationPolicy.prototype.sendRequest = function (webResource) {
        return __awaiter(this, void 0, void 0, function () {
            var token;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!webResource.headers)
                            webResource.headers = new HttpHeaders();
                        return [4 /*yield*/, this.getToken({
                                abortSignal: webResource.abortSignal,
                                tracingOptions: {
                                    spanOptions: webResource.spanOptions
                                }
                            })];
                    case 1:
                        token = _a.sent();
                        webResource.headers.set(Constants.HeaderConstants.AUTHORIZATION, "Bearer " + token);
                        return [2 /*return*/, this._nextPolicy.sendRequest(webResource)];
                }
            });
        });
    };
    BearerTokenAuthenticationPolicy.prototype.getToken = function (options) {
        return __awaiter(this, void 0, void 0, function () {
            var accessToken;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        accessToken = this.tokenCache.getCachedToken();
                        if (!(accessToken === undefined)) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.credential.getToken(this.scopes, options)];
                    case 1:
                        accessToken = (_a.sent()) || undefined;
                        this.tokenCache.setCachedToken(accessToken);
                        _a.label = 2;
                    case 2: return [2 /*return*/, accessToken ? accessToken.token : undefined];
                }
            });
        });
    };
    return BearerTokenAuthenticationPolicy;
}(BaseRequestPolicy));
export { BearerTokenAuthenticationPolicy };
//# sourceMappingURL=bearerTokenAuthenticationPolicy.js.map